import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Link, MenuItem, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { CorporateTaxService } from '../../../services/corporateTax.service';
import {
    CREDIT_CATEGORIES, CreditCategory, CreditStatus, LedgerRow, SetOff, TaxComputation, TaxCreditLedger,
} from '../../../interfaces/corporateTax.interface';
import { useToast } from '../../../context/ToastContext';
import { formatMoney, humanizeEnum } from '../../../utils/format';
import { formatDate, toLocalIsoDate } from '../../../utils/dateUtils';
import ConfirmDialog from '../../common/ConfirmDialog';
import {
    CATEGORY_LABEL, currentIncomeYear, documentHref, incomeYearLabel, incomeYearOptions,
} from './corporateTaxText';

interface Props { institutionId: string; startMonth: number; canManage: boolean; canPost: boolean }

const filterCell = { flex: '1 1 180px', maxWidth: 240 } as const;

/** The AIT / source-tax credit ledger for one income year, with evidence editing and AIT set-off. */
const LedgerTab: React.FC<Props> = ({ institutionId, startMonth, canManage, canPost }) => {
    const toast = useToast();
    const [incomeYear, setIncomeYear] = useState(currentIncomeYear(startMonth));
    const [category, setCategory] = useState<CreditCategory | ''>('');
    const [status, setStatus] = useState<CreditStatus | ''>('');
    const [ledger, setLedger] = useState<TaxCreditLedger | null>(null);
    const [setOffs, setSetOffs] = useState<SetOff[]>([]);
    const [posted, setPosted] = useState<TaxComputation[]>([]);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [busy, setBusy] = useState(false);
    const [edit, setEdit] = useState<LedgerRow | null>(null);
    const [evidence, setEvidence] = useState({ certificateNumber: '', certificateDate: '', issuer: '', notes: '' });
    const [setOffOpen, setSetOffOpen] = useState(false);
    const [computationId, setComputationId] = useState('');
    const [setOffDate, setSetOffDate] = useState(toLocalIsoDate());
    const [confirmReverse, setConfirmReverse] = useState<SetOff | null>(null);

    const load = useCallback(async () => {
        try {
            const [l, s, c] = await Promise.all([
                CorporateTaxService.ledger(institutionId, incomeYear, category, status),
                CorporateTaxService.listSetOffs(institutionId),
                CorporateTaxService.listComputations(institutionId),
            ]);
            setLedger(l);
            setSetOffs(s);
            setPosted(c.filter(x => x.status === 'POSTED'));
            setSelected(new Set());
        } catch (e) {
            toast.showApiError(e, 'Failed to load the AIT ledger');
        }
    }, [institutionId, incomeYear, category, status]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const selectable = useMemo(() => (ledger?.rows ?? []).filter(r => r.status === 'OPEN'), [ledger]);
    const selectedTotal = useMemo(
        () => selectable.filter(r => selected.has(r.lineId)).reduce((a, r) => a + r.amount, 0), [selectable, selected]);

    const toggle = (id: string) => setSelected(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const openEvidence = (row: LedgerRow) => {
        setEdit(row);
        setEvidence({
            certificateNumber: row.certificateNumber ?? '', certificateDate: row.certificateDate ?? '',
            issuer: row.issuer ?? '', notes: row.notes ?? '',
        });
    };

    const saveEvidence = async () => {
        if (!edit) return;
        setBusy(true);
        try {
            await CorporateTaxService.saveEvidence(institutionId, edit.lineId, {
                certificateNumber: evidence.certificateNumber || null, certificateDate: evidence.certificateDate || null,
                issuer: evidence.issuer || null, notes: evidence.notes || null,
            });
            toast.showSuccess('Evidence saved');
            setEdit(null);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to save the evidence');
        } finally {
            setBusy(false);
        }
    };

    const doSetOff = async () => {
        setBusy(true);
        try {
            await CorporateTaxService.createSetOff(institutionId, {
                computationId, setOffDate, lineIds: Array.from(selected),
            });
            toast.showSuccess('AIT set off against the tax computation');
            setSetOffOpen(false);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to set off the AIT');
        } finally {
            setBusy(false);
        }
    };

    const doReverse = async () => {
        if (!confirmReverse) return;
        setBusy(true);
        try {
            await CorporateTaxService.reverseSetOff(institutionId, confirmReverse.id);
            toast.showSuccess('Set-off reversed; the credits are open again');
            setConfirmReverse(null);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to reverse the set-off');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box data-help-section="accounting.corpTax.ledger.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <TextField data-help="accounting.corpTax.ledger.year" select size="small" label="Income year" value={incomeYear} sx={filterCell}
                    onChange={e => setIncomeYear(Number(e.target.value))}>
                    {incomeYearOptions(startMonth).map(y => <MenuItem key={y} value={y}>{incomeYearLabel(y, startMonth)}</MenuItem>)}
                </TextField>
                <TextField data-help="accounting.corpTax.ledger.category" select size="small" label="Category" value={category} sx={filterCell}
                    onChange={e => setCategory(e.target.value as CreditCategory | '')}>
                    <MenuItem value="">All categories</MenuItem>
                    {CREDIT_CATEGORIES.map(c => <MenuItem key={c} value={c}>{CATEGORY_LABEL[c]}</MenuItem>)}
                </TextField>
                <TextField data-help="accounting.corpTax.ledger.status" select size="small" label="Status" value={status} sx={filterCell}
                    onChange={e => setStatus(e.target.value as CreditStatus | '')}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="OPEN">Open</MenuItem>
                    <MenuItem value="SET_OFF">Set off</MenuItem>
                </TextField>
                {canPost && (
                    <Button data-help="accounting.corpTax.ledger.setOff" variant="contained" disabled={busy || selected.size === 0}
                        onClick={() => { setComputationId(posted[0]?.id ?? ''); setSetOffOpen(true); }}>
                        Set off selected ({formatMoney(selectedTotal)})
                    </Button>
                )}
            </Box>

            {ledger && ledger.unmappedCount > 0 && (
                <Alert severity="warning">
                    {ledger.unmappedCount} line(s) are in the "Other / unmapped" category: their journals are not from a
                    document that tags the tax. They still count as credits; check them against your certificates.
                </Alert>
            )}

            {ledger && (
                <TableContainer component={Paper}>
                    <Table data-help-section="accounting.corpTax.ledger.totals" size="small" aria-label="Credit totals by category">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="accounting.corpTax.ledger.totals.col.category">Category</TableCell><TableCell data-help="accounting.corpTax.ledger.totals.col.lines" align="right">Lines</TableCell>
                                <TableCell data-help="accounting.corpTax.ledger.totals.col.total" align="right">Total</TableCell><TableCell data-help="accounting.corpTax.ledger.totals.col.open" align="right">Open</TableCell>
                                <TableCell data-help="accounting.corpTax.ledger.totals.col.setOff" align="right">Set off</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {ledger.totals.map(t => (
                                <TableRow key={t.category}>
                                    <TableCell>{CATEGORY_LABEL[t.category]}</TableCell>
                                    <TableCell align="right">{t.lineCount}</TableCell>
                                    <TableCell align="right">{formatMoney(t.total)}</TableCell>
                                    <TableCell align="right">{formatMoney(t.open)}</TableCell>
                                    <TableCell align="right">{formatMoney(t.setOff)}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell><strong>All credits</strong></TableCell><TableCell />
                                <TableCell align="right" />
                                <TableCell align="right"><strong>{formatMoney(ledger.openTotal)}</strong></TableCell>
                                <TableCell align="right"><strong>{formatMoney(ledger.setOffTotal)}</strong></TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <TableContainer component={Paper}>
                <Table data-help-section="accounting.corpTax.ledger.lines" size="small" aria-label="AIT credit lines">
                    <TableHead>
                        <TableRow>
                            {canPost && <TableCell data-help="accounting.corpTax.ledger.col.select" padding="checkbox" />}
                            <TableCell data-help="accounting.corpTax.ledger.col.date">Date</TableCell><TableCell data-help="accounting.corpTax.ledger.col.document">Document</TableCell><TableCell data-help="accounting.corpTax.ledger.col.account">Account</TableCell>
                            <TableCell data-help="accounting.corpTax.ledger.col.category">Category</TableCell><TableCell data-help="accounting.corpTax.ledger.col.amount" align="right">Amount</TableCell>
                            <TableCell data-help="accounting.corpTax.ledger.col.certificate">Certificate</TableCell><TableCell data-help="accounting.corpTax.ledger.col.status">Status</TableCell><TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(ledger?.rows ?? []).map(r => (
                            <TableRow key={r.lineId} hover>
                                {canPost && (
                                    <TableCell padding="checkbox">
                                        {r.status === 'OPEN' && (
                                            <Checkbox data-help="accounting.corpTax.ledger.rowSelect" size="small" checked={selected.has(r.lineId)} onChange={() => toggle(r.lineId)}
                                                inputProps={{ 'aria-label': `Select ${r.entryNumber}` }} />
                                        )}
                                    </TableCell>
                                )}
                                <TableCell>{formatDate(r.date)}</TableCell>
                                <TableCell>
                                    {documentHref(r.referenceType, r.referenceId, r.entryId)
                                        ? <Link component={RouterLink} to={documentHref(r.referenceType, r.referenceId, r.entryId)!}>{r.entryNumber}</Link>
                                        : r.entryNumber}
                                    <Typography variant="caption" display="block" color="text.secondary">
                                        {humanizeEnum(r.referenceType)}
                                    </Typography>
                                </TableCell>
                                <TableCell>{r.accountCode} {r.accountName}</TableCell>
                                <TableCell>{CATEGORY_LABEL[r.category]}</TableCell>
                                <TableCell align="right">{formatMoney(r.amount)}</TableCell>
                                <TableCell>
                                    {r.certificateNumber ?? '-'}
                                    {r.certificateDate && <Typography variant="caption" display="block">{formatDate(r.certificateDate)}</Typography>}
                                </TableCell>
                                <TableCell>
                                    {r.status ? <Chip size="small" label={humanizeEnum(r.status)} color={r.status === 'OPEN' ? 'warning' : 'success'} /> : '-'}
                                </TableCell>
                                <TableCell>
                                    {canManage && r.status && <Button data-help="accounting.corpTax.ledger.action.evidence" size="small" onClick={() => openEvidence(r)}>Evidence</Button>}
                                </TableCell>
                            </TableRow>
                        ))}
                        {ledger && ledger.rows.length === 0 && (
                            <TableRow><TableCell colSpan={9}>No credit lines for this selection.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle1">Set-offs</Typography>
            <TableContainer component={Paper}>
                <Table data-help-section="accounting.corpTax.ledger.setOffs" size="small" aria-label="AIT set-offs">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="accounting.corpTax.ledger.so.col.date">Date</TableCell><TableCell data-help="accounting.corpTax.ledger.so.col.amount" align="right">Amount</TableCell><TableCell data-help="accounting.corpTax.ledger.so.col.journal">Journal</TableCell>
                            <TableCell data-help="accounting.corpTax.ledger.so.col.status">Status</TableCell><TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {setOffs.map(s => (
                            <TableRow key={s.id}>
                                <TableCell>{formatDate(s.setOffDate)}</TableCell>
                                <TableCell align="right">{formatMoney(s.amount)}</TableCell>
                                <TableCell>
                                    {s.journalEntryId
                                        ? <Link component={RouterLink} to={`/accounting/journal-entries/${s.journalEntryId}`}>View journal</Link> : '-'}
                                </TableCell>
                                <TableCell>{s.reversedAt ? 'Reversed' : 'Active'}</TableCell>
                                <TableCell>
                                    {canPost && !s.reversedAt && <Button data-help="accounting.corpTax.ledger.action.reverseSetOff" size="small" color="error" onClick={() => setConfirmReverse(s)}>Reverse</Button>}
                                </TableCell>
                            </TableRow>
                        ))}
                        {setOffs.length === 0 && <TableRow><TableCell colSpan={5}>No set-offs yet.</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog data-help-page="accounting.corpTax.ledger.evidence.page" open={edit !== null} onClose={() => setEdit(null)} fullWidth maxWidth="sm">
                <DialogTitle>Tax credit evidence</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <TextField data-help="accounting.corpTax.ledger.evidence.number" label="Certificate number" value={evidence.certificateNumber} sx={{ mt: 1 }} fullWidth
                        onChange={e => setEvidence({ ...evidence, certificateNumber: e.target.value })} />
                    <TextField data-help="accounting.corpTax.ledger.evidence.date" label="Certificate date" type="date" value={evidence.certificateDate} fullWidth InputLabelProps={{ shrink: true }}
                        onChange={e => setEvidence({ ...evidence, certificateDate: e.target.value })} />
                    <TextField data-help="accounting.corpTax.ledger.evidence.issuer" label="Issuer (who deducted the tax)" value={evidence.issuer} fullWidth
                        onChange={e => setEvidence({ ...evidence, issuer: e.target.value })} />
                    <TextField data-help="accounting.corpTax.ledger.evidence.notes" label="Notes" value={evidence.notes} multiline minRows={2} fullWidth
                        onChange={e => setEvidence({ ...evidence, notes: e.target.value })} />
                </DialogContent>
                <DialogActions>
                    <Button data-help="accounting.corpTax.ledger.evidence.cancel" onClick={() => setEdit(null)}>Cancel</Button>
                    <Button data-help="accounting.corpTax.ledger.evidence.save" variant="contained" disabled={busy} onClick={saveEvidence}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog data-help-page="accounting.corpTax.ledger.setOffForm.page" open={setOffOpen} onClose={() => setSetOffOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Set off AIT against assessed tax</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <Typography variant="body2">
                        Sets off {selected.size} credit line(s) totalling {formatMoney(selectedTotal)}: debits income tax payable
                        and credits the AIT accounts.
                    </Typography>
                    {posted.length === 0 && <Alert severity="warning">Post a tax computation first: a set-off settles a posted provision.</Alert>}
                    <TextField data-help="accounting.corpTax.ledger.setOffForm.computation" select label="Against computation" value={computationId} fullWidth
                        onChange={e => setComputationId(e.target.value)}>
                        {posted.map(c => (
                            <MenuItem key={c.id!} value={c.id!}>
                                {formatDate(c.fiscalYearStart)} to {formatDate(c.fiscalYearEnd)}: {formatMoney(c.taxCharge)}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField data-help="accounting.corpTax.ledger.setOffForm.date" label="Set-off date" type="date" value={setOffDate} fullWidth InputLabelProps={{ shrink: true }}
                        onChange={e => setSetOffDate(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button data-help="accounting.corpTax.ledger.setOffForm.cancel" onClick={() => setSetOffOpen(false)}>Cancel</Button>
                    <Button data-help="accounting.corpTax.ledger.setOffForm.submit" variant="contained" disabled={busy || !computationId || !setOffDate} onClick={doSetOff}>Post set-off</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog open={confirmReverse !== null} title="Reverse AIT set-off?"
                message="This posts a reversing journal and makes the credit lines open again."
                confirmLabel="Reverse" confirmColor="error" submitting={busy}
                onCancel={() => setConfirmReverse(null)} onConfirm={doReverse} />
        </Box>
    );
};

export default LedgerTab;
